import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import PropTypes from 'prop-types';

import OutputText from '@ey/canvascoreservices/OutputText';
import {IconoirInfoCircle} from '@ey-xd/motif-icon';

import {currentResource, labels} from '../../../util/utils';
import {assertionOrder, groupRiskStatus, inherentRiskTypes} from '../../../util/uiconstants';

import {updateGroupComponentAccount} from '../../../actions/Reconciliations';
import selectMappedComponentAccounts from '../../../reducers/Reconciliations/selectors/selectMappedComponentAccounts';

import Accordion from '../../CoreWrappers/Accordion';
import Button from '../../CoreWrappers/Button';
import ShowMoreLess from '../../CoreWrappers/ShowMoreLess';
import AccountAndRiskDetailsFooterMessage from '../AccountAndRiskDetailsFooterMessage';
import PspIndexCountDropdown from '../PspIndexCountDropdown';
import HrAlt from '../Hr/HrAlt';
import RelatedRiskIndicator from '../RelatedRiskIndicator';
import StyledAccountDetailsGrid, {StyledAccountDetailsGridContent} from './StyledAccountDetailsGrid';
import StyledAccordionContent from './StyledAccordionContent';
import StyledAccountDetails from './StyledAccountDetails';
import StyledFooterHeader from './StyledFooterHeader';
import StyledCommentHeader from './StyledCommentHeader';
import CommentModal from '../CommentModal';
import DeleteCommentModal from '../DeleteCommentModal';
import RelateAccountsModal from './RelateAccountsModal';
import selectProjectAndEngIds from '../../../reducers/Reconciliations/selectors/selectProjectAndEngIds';

const AccountDetails = ({primaryAccount = null, componentAccounts = null, onGroupRiskStatusChange = () => {}}) => {
	if (!primaryAccount) return false;

	const {groupComponentId, rationale, accountId, groupRiskStatusId} = primaryAccount;

	const [open, setOpen] = useState(groupRiskStatusId === groupRiskStatus.systemReOpened);
	const [showRelateModal, setShowRelateModal] = useState(false);

	const dispatch = useDispatch();

	const mappedComponentAccounts = selectMappedComponentAccounts(accountId);
	const apiConfig = selectProjectAndEngIds();

	const hasGroupComponentAccount = groupComponentId && rationale;
	const hasMappedAccounts = mappedComponentAccounts?.length > 0;
	const isOpenStatus = [groupRiskStatus.open, groupRiskStatus.systemReOpened].includes(groupRiskStatusId);

	const renderPSPInformation = (pspIndexes) => {
		const props = {
			itemList: pspIndexes,
			activeLabel: pspIndexes?.length,
			headerText: labels.pspIndexes,
			maxItems: 6
		};

		return <PspIndexCountDropdown {...props} />;
	};

	const handleSaveComment = (rationale) => {
		const payload = {
			rationale,
			concurrencyToken: primaryAccount.concurrencyToken
		};

		return dispatch(
			updateGroupComponentAccount(primaryAccount.accountId, primaryAccount.groupComponentAccountId, payload, apiConfig)
		);
	};

	const renderCommentModal = (isEdit = false) => {
		const props = {
			isEdit: isEdit,
			rationale: primaryAccount.rationale,
			rationaleModifyDate: primaryAccount.rationaleModifyDate,
			rationaleModifyBy: primaryAccount.rationaleModifyBy,
			onSaveComment: handleSaveComment
		};

		return (
			<div onClick={(e) => e.stopPropagation()} className="create-comment-container">
				<CommentModal {...props} />
			</div>
		);
	};

	const renderDeleteCommentModal = () => {
		const props = {
			labels: currentResource.deleteCommentModalLabels,
			onSave: handleSaveComment
		};

		return <DeleteCommentModal {...props} />;
	};

	const renderRelateAccountsModal = () => {
		const props = {
			primaryAccount,
			componentAccounts,
			show: showRelateModal,
			setShow: setShowRelateModal
		};
		return <RelateAccountsModal {...props} />;
	};

	const renderAccountInfo = ({accountName, accountDesignation, assertions, pspIndexes}, isHeader = false) => {
		const designationText = currentResource.accountDesignationsLabels[accountDesignation];
		const currentIndexes = pspIndexes?.length > 0 ? [...pspIndexes] : [];
		return (
			<>
				<OutputText body2 light={!isHeader} ellipsis title={accountName}>
					{accountName}
				</OutputText>
				{isHeader ? <div /> : renderPSPInformation(currentIndexes)}
				<OutputText body2 light={!isHeader} ellipsis title={designationText}>
					{designationText}
				</OutputText>
				{assertionOrder.map((item) => {
					const findAssertion = (assertion) => assertion.assertionAbbreviation === item;

					const props = {};

					const foundAssertion = assertions?.find(findAssertion);
					if (foundAssertion?.inherentRiskTypeId !== null)
						props.relatedRisk = inherentRiskTypes[foundAssertion?.inherentRiskTypeId];

					return <RelatedRiskIndicator {...props} />;
				})}
			</>
		);
	};

	const renderAccordionHeader = () => {
		return (
			<StyledAccountDetailsGrid>
				{renderAccountInfo(primaryAccount, true)}
				{!hasGroupComponentAccount && renderCommentModal()}
			</StyledAccountDetailsGrid>
		);
	};

	const renderMappedAccounts = () => {
		return (
			<>
				<HrAlt $color="--neutrals-200" />
				{mappedComponentAccounts.map((account) => (
					<StyledAccountDetailsGridContent>{renderAccountInfo(account)}</StyledAccountDetailsGridContent>
				))}
			</>
		);
	};

	const renderTrigger = ({size = 'small', label, onClick = null}) => {
		const props = {
			variant: 'text-alt',
			label,
			size
		};

		if (onClick) props.onClick = onClick;

		return <Button {...props} />;
	};

	const renderEditComment = (open) => {
		if (!hasGroupComponentAccount) return false;

		return (
			<>
				<HrAlt $color="--neutrals-200" />
				<StyledCommentHeader>
					<OutputText body2 left>
						{labels.commentTitle}
					</OutputText>
					{renderCommentModal(true)}
					{renderDeleteCommentModal()}
				</StyledCommentHeader>
				{open && <ShowMoreLess text={rationale} numLines={3} />}
			</>
		);
	};

	const renderFooterInfo = () => {
		let props = {
			renderTrigger: () =>
				renderTrigger({
					label: labels.relateAccounts,
					onClick: () => setShowRelateModal(true)
				}),
			message: labels.mapAccountsMessage,
			color: 'orange'
		};

		if (hasMappedAccounts && groupRiskStatusId !== groupRiskStatus.assessed) {
			props = {
				renderTrigger: () =>
					renderTrigger({
						label: labels.markAssessed,
						onClick: () =>
							onGroupRiskStatusChange && onGroupRiskStatusChange(primaryAccount.accountId, groupRiskStatus.assessed)
					}),
				icon: IconoirInfoCircle,
				color: 'black'
			};
			if (primaryAccount.groupRiskStatusId === groupRiskStatus.open) props.message = labels.teamAccountMappedMessage;
			if (primaryAccount.groupRiskStatusId === groupRiskStatus.systemReOpened)
				props.message = labels.teamAccountWasPreviousAssessedMessage;
		}

		if (groupRiskStatusId === groupRiskStatus.assessed) {
			props = {
				renderTrigger: () =>
					renderTrigger({
						label: labels.reopenAssessedAccount,
						onClick: () =>
							onGroupRiskStatusChange && onGroupRiskStatusChange(primaryAccount.accountId, groupRiskStatus.open)
					}),
				icon: IconoirInfoCircle,
				color: 'black',
				message: labels.reopenAssessedAccountMessage
			};
		}

		return (
			<>
				<HrAlt $color="--neutrals-200" />
				<AccountAndRiskDetailsFooterMessage {...props} />
			</>
		);
	};

	const props = {
		open,
		onOpen: () => setOpen(true),
		onClose: () => setOpen(false),
		trigger: renderAccordionHeader
	};

	return (
		<StyledAccountDetails>
			<Accordion {...props}>
				<StyledAccordionContent>
					<StyledFooterHeader>
						<OutputText body2 light>
							{labels.componentTeamAccounts}
						</OutputText>
						{isOpenStatus && (
							<>
								{renderTrigger({
									label: labels.relateAccounts,
									onClick: () => setShowRelateModal(true)
								})}
								{renderRelateAccountsModal()}
							</>
						)}
					</StyledFooterHeader>
					{hasMappedAccounts && renderMappedAccounts()}
					{renderEditComment(open)}
					{renderFooterInfo()}
				</StyledAccordionContent>
			</Accordion>
		</StyledAccountDetails>
	);
};

const accountShape = {
	accountId: PropTypes.string.isRequired,
	accountName: PropTypes.string,
	accountDesignation: PropTypes.oneOf([1, 2, 3, 4, 5]),
	assertions: PropTypes.arrayOf(
		PropTypes.shape({
			assertionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
			assertionAbbreviation: PropTypes.string,
			isHigherRisk: PropTypes.bool
		})
	)
};

AccountDetails.propTypes = {
	primaryAccount: PropTypes.shape({
		...accountShape,
		relatedAccounts: PropTypes.arrayOf(PropTypes.shape({...accountShape}))
	})
};

export default AccountDetails;
